import { FUTURES_PRODUCTS, SPOT_PRODUCTS } from 'constants/actionTypes';
import { createLoadingSelector } from 'erisxkit/client';
import { useAppSelector } from 'hooks/useAppSelector';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  futuresProducts,
  getFuturesProductsOptions,
} from 'reducers/futuresProductReducer';
import {
  getSpotProductsOptions,
  spotProducts,
} from 'reducers/spotProductsReducer';
import {
  Dropdown,
  Form,
  FormFieldProps,
  StrictDropdownProps,
  DropdownItemProps,
  Select,
  DropdownProps,
} from 'semantic-ui-react';
import { flatten, isEmpty, union } from 'lodash';

export const dividedOptions = (categories: DropdownItemProps[] = []) =>
  flatten(
    categories.map((category) =>
      !isEmpty(category) && !category.hide
        ? union(
            [
              {
                key: category.key,
                as: () => (
                  <Dropdown.Header
                    key={category.key}
                    content={category.header}
                  />
                ),
              },
            ],
            [
              {
                key: `${category.key}-divider`,
                as: () => <Dropdown.Divider key={`${category.key}-divider`} />,
              },
            ],
            category.options,
          )
        : [],
    ),
  );

type Props = { name?: string } & FormFieldProps & StrictDropdownProps;

const ProductsDropdown = ({
  label = 'Product',
  name = 'productSymbol',
  placeholder = 'Select Product...',
  onChange,
  value,
}: Props) => {
  const dispatch = useDispatch();
  const loadingSpot = useAppSelector(createLoadingSelector([SPOT_PRODUCTS]));
  const loadingFutures = useAppSelector(
    createLoadingSelector([FUTURES_PRODUCTS]),
  );
  const spotOptions = useAppSelector(getSpotProductsOptions);
  const futuresOptions = useAppSelector(getFuturesProductsOptions);

  const options = useMemo(
    () =>
      dividedOptions([
        {
          header: 'Futures Products',
          options: futuresOptions,
          key: 'futuresFilter',
        },
        {
          header: 'Spot Products',
          options: spotOptions,
          key: 'spotFilter',
        },
      ]),
    [spotOptions, futuresOptions],
  );
  const loading = useMemo(
    () => loadingSpot || loadingFutures,
    [loadingSpot, loadingFutures],
  );

  useEffect(() => {
    dispatch(futuresProducts());
    dispatch(spotProducts());
  }, [dispatch]);

  return (
    <Form.Field
      label={label}
      name={name}
      id={name}
      placeholder={placeholder}
      control={Dropdown}
      options={options}
      loading={loading}
      disabled={loading}
      selection
      search
      clearable
      onChange={(
        event: React.SyntheticEvent<HTMLElement>,
        props: DropdownProps,
      ) => {
        if (onChange) {
          onChange(event, props);
        }
      }}
      value={value}
    />
  );
};

export default ProductsDropdown;
